import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useSelector } from "../../../store";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import Category from "../../../types/category";
import AddEditCategoryForm from "../../forms/AddEditCategoryForm";
import SettingsModal from "../../../types/settingsModal";
import { Base } from "../../base";
import ColumnDef from "../../../types/columnDef";
import SearchContainer from "./SearchContainer";

interface Props {
  apiCategorySearch: (search: SearchParams) => Promise<any>;
  apiGetCategory: (id: number) => void;
  resetCategory: () => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setCategoryFilter: (params: SearchParams) => void;
  deleteModal: SettingsModal;
}

const Categories: React.FC<Props> = (props) => {
  const {
    apiCategorySearch,
    apiGetCategory,
    resetCategory,
    phone,
    setDeleteModal,
    setCategoryFilter,
    deleteModal,
  } = props;
  const { categorySearchResults, categoryFilter, status } = useSelector(
    (store) => store.category
  );
  const [addEditModal, setAddEditModal] = useState(false);
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [highlightId, setHighlightId] = useState(0);

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setCategoryFilter(defaultParams);
    };
  }, [phone]);

  useEffect(() => {
    if (
      !deleteModal.visible &&
      deleteModal.modal === undefined &&
      highlightId > 0
    ) {
      setHighlightId(0);
    }
  }, [deleteModal.modal, deleteModal.visible, highlightId]);

  const updateSearch = debounce((query: string) => {
    setCategoryFilter({
      ...categoryFilter,
      query: query,
      pageNumber: phone ? 2 : 1,
      pageSize: 10,
    });
    apiCategorySearch({
      ...categoryFilter,
      pageNumber: 1,
      pageSize: 10,
      query: query,
    }).then((result: any) => {
      setMobileRows(result);
    });
  }, 500);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Category",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "jobType",
      headerName: "Job Type",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((category: Category) => {
      const getJobTypeAbv = () => {
        if (category.jobType?.name?.includes(" ")) {
          return `${category?.jobType?.name?.split(" ")[0][0]}${
            category?.jobType?.name?.split(" ")[1][0]
          }`;
        } else return `${category?.jobType?.name?.split(" ")[0][0]}`;
      };

      let rowObject = {
        id: category.id,
        name: category.name,
        mobileTitle: `${getJobTypeAbv()}: ${category.name}`,
        jobType: category?.jobType?.name,
        actions: (
          <Base.TableActions
            id={category.id}
            // TODO: Lock behind when we add roles
            handleDelete={() => {
              apiGetCategory(category.id);
              setDeleteModal({ visible: true, modal: "categories" });
              setHighlightId(category.id);
            }}
            handleEdit={() => {
              apiGetCategory(category.id);
              setAddEditModal(true);
              setHighlightId(category.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiCategorySearch({
      ...categoryFilter,
      pageNumber,
      pageSize,
    }).then(() =>
      setCategoryFilter({
        ...categoryFilter,
        pageNumber,
        pageSize,
      })
    );
  };

  const next = () => {
    apiCategorySearch(categoryFilter).then((result: any) => {
      setCategoryFilter({
        ...categoryFilter,
        pageNumber: categoryFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };

  const close = () => {
    apiCategorySearch(defaultParams);
    setCategoryFilter(defaultParams);
    setAddEditModal(false);
    resetCategory();
    setHighlightId(0);
  };

  const handleSaveUpdate = () => {
    apiCategorySearch({ query: "", pageNumber: 1, pageSize: 10 }).then(
      (result: any) => {
        setMobileRows(result);
        setCategoryFilter({ ...categoryFilter, pageNumber: 2 });
      }
    );
  };

  const getOrderBy = (field: string) => {
    const fieldList = ["name", "jobType"];
    return fieldList.find((value) => value === field) ?? undefined;
  };

  return (
    <>
      <SearchContainer
        updateSearch={updateSearch}
        searchValue={categoryFilter.query}
        modalOpen={setAddEditModal}
        reset={resetCategory}
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(categorySearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={categoryFilter.pageNumber}
          pageSize={categoryFilter.pageSize}
          totalRows={categorySearchResults?.totalResults}
          changePage={changePage}
          next={next}
          loading={status === "loading"}
          handleSort={(orderBy, orderDirection) => {
            const orderByField = orderBy ? getOrderBy(orderBy) : undefined;
            apiCategorySearch({
              ...categoryFilter,
              orderBy: orderByField,
              orderDirection,
            });
          }}
          highlightId={highlightId}
        />
      </SearchContainer>
      <Base.Modal open={addEditModal} onClose={close}>
        <AddEditCategoryForm close={close} onSave={handleSaveUpdate} />
      </Base.Modal>
    </>
  );
};

export default Categories;
